// Case-study content — universal template, one entry per venture.

export const CASES = {
  quantumeye: {
    slug: "quantumeye",
    name: "QuantumEye",
    accent: "#00D4AA",
    accentTint: "rgba(0, 212, 170, 0.10)",
    status: "PAST VENTURE",
    statusColor: "slate",
    domain: "EDGE AI · COMPUTER VISION",
    period: "2025 — 2026",
    tagline: "Real-time retail security, built on edge AI.",
    role: "Co-Founder · Product & Tech Lead",
    heroPhoto: {
      src: "/images/QuantumEyeHero.jpg",
      alt: "Sam Erpik at the QuantumEye booth, Retail Tech Show 2025",
      caption:
        "Live at the Retail Tech Show 2025 — the QuantumEye booth, on the day we found our first paid pilot.",
    },
    problem:
      "Retail loss prevention is stuck in 2010. Cameras are everywhere, but none of them think. Stores carry the cost of theft, staff time, and incident reports — while the data sits unused on a DVR in a back room. We asked a simple question: what if every camera could understand what it was watching, in real time, without sending video to the cloud?",
    built: [
      "A multimodal vision pipeline that turns archived feeds and live streams into searchable, structured intelligence — like grep, for what the world looks like.",
      "On-device inference on Jetson hardware so footage never left the store; latency was sub-200ms.",
      "A dashboard for store managers that surfaces only the alerts that matter — not the noise.",
      "Privacy-by-design from day one: faces were never stored, only event signatures.",
    ],
    process: [
      { n: "01", title: "Field-walked the problem", body: "Two weeks shadowing in stores before a single line of code." },
      { n: "02", title: "Spec'd the model and the team", body: "Shortlist of 4 vision backbones. Picked the one that fit the smallest hardware budget." },
      { n: "03", title: "Shipped the spike", body: "Working end-to-end MVP in 3 weeks — ugly, but real." },
      { n: "04", title: "Found 3 design partners", body: "Cold-emailed loss-prevention leads. Two said yes the same week." },
      { n: "05", title: "Showed up at Retail Tech 2025", body: "Demoed live. 8 conversations turned into a paid pilot." },
      { n: "06", title: "Follow-on pilots", body: "Ran follow-on pilots, refined the commercial model, and built the case for broader retail rollout." },
    ],
    tech: ["React", "TypeScript", "Python", "ONNX", "Jetson Nano", "Postgres", "Edge ML", "Docker"],
    proof: [
      { value: "3", label: "design partners" },
      { value: "9 wks", label: "from zero to first paid pilot" },
      { value: "Retail Tech '25", label: "live demo on the show floor" },
    ],
    learned:
      "Building hardware-adjacent products taught me more about distribution than five years of B2B SaaS. The best product on the wrong shelf still loses. We didn't win design partners by being technically impressive — we won them by showing up at their trade show, in their language, ready to install on their hardware in 30 minutes.",
    next: "purpleluna",
    prev: "whotnow",
  },

  purpleluna: {
    slug: "purpleluna",
    name: "Purple Luna",
    website: "https://purpleluna.co.uk",
    accent: "#8B5CF6",
    accentTint: "rgba(139, 92, 246, 0.10)",
    status: "ACTIVE",
    statusColor: "violet",
    domain: "AI RECEPTIONIST · WHATSAPP",
    period: "2021 — Present",
    tagline: "An AI receptionist that runs the front desk for appointment businesses — on WhatsApp.",
    role: "Founder",
    problem:
      "Purple Luna spent years as a digital agency, building websites, products and digital experiences for small service businesses one bespoke project at a time. Good work — but it only ever reached the clients who could afford a custom build. Then AI changed what a small team could ship, so we did the obvious thing: we took everything we'd learned about serving these businesses and turned it into a product. Luna is an AI receptionist that answers your business's WhatsApp — the channel your customers already use — books appointments, takes deposits, and runs the front desk 24/7, in your own voice. The agency built one great experience at a time. Luna gives every salon, dental practice, clinic and gym the same thing, switched on in an afternoon.",
    built: [
      "Answers your business WhatsApp in seconds, as your business — warm and on-brand, never a robotic auto-reply.",
      "Books appointments straight into your calendar, offers real open slots, and holds them with a deposit.",
      "Takes payment the safe way: a secure link, never card details typed into the chat.",
      "Knows your services, prices, hours and policies — and hands the conversation to you the moment it should, on anything about money, health, or a real judgement call.",
      "Works while you're mid-appointment, closed, or asleep. No missed message, no walked-away booking.",
    ],
    process: [
      { n: "01", title: "Connect your WhatsApp", body: "Luna plugs into your existing WhatsApp Business number. Nothing for customers to download." },
      { n: "02", title: "Teach her the business", body: "Services, prices, hours, the questions you answer fifty times a week. A few minutes, once." },
      { n: "03", title: "She answers, books, collects", body: "Customers message like they always do. Luna replies, books the slot, takes the deposit." },
      { n: "04", title: "You get the clean handovers", body: "Anything that needs a human lands on your phone with the full context attached." },
      { n: "05", title: "You get your evenings back", body: "The front desk runs itself. You do the work you're actually good at." },
    ],
    tech: ["WhatsApp Business API", "Claude / LLMs", "Secure payment links", "Calendar sync", "Guardrails", "React", "Node"],
    proof: [
      { value: "24/7", label: "never misses a message" },
      { value: "< 60s", label: "average reply, day or night" },
      { value: "£0", label: "card details ever stored in chat" },
    ],
    learned:
      "The best of the agency years was never the code — it was the judgement: knowing what a small business actually needs, and saying it in their voice. AI doesn't replace that taste; it lets us package it. Purple Luna used to build one great experience at a time. Now it runs, quietly, for every business at once.",
    next: "whotnow",
    prev: "quantumeye",
  },

  whotnow: {
    slug: "whotnow",
    name: "WhotNow",
    accent: "#FF6B35",
    accentTint: "rgba(255, 107, 53, 0.10)",
    status: "GOOD LESSONS",
    statusColor: "slate",
    domain: "CONSUMER · NIGHTLIFE · VENUES",
    period: "2016 — 2018",
    tagline: "Real-time discovery for the city that's already happening.",
    role: "Co-Founder · Product Lead",
    problem:
      "Going out shouldn't require a 12-tab research project. WhotNow was a phone-shaped answer to 'what's actually on tonight — bar, club, restaurant, cafe — near me, that I'd actually go to.' Built before the AI hype cycle, when matching the right humans to the right room was still a hard product problem.",
    built: [
      "Live discovery feed for nightlife, restaurants, bars, and cafes across Brighton & Hove — sourced from venues, promoters, and a small army of local editors.",
      "A taste graph that understood you within 3 nights of using the app — not 30.",
      "A two-sided platform: venues posted, users showed up, and the data flowed both ways.",
      "iOS + Android with shared product surface. One product designer (me). One engineering team. Zero tolerance for feature bloat.",
    ],
    process: [
      { n: "01", title: "Won a startup competition", body: "WhotNow started as an MSc project, won a competition, then became a real company. The best kind of forcing function." },
      { n: "02", title: "Sketched the night out", body: "Mapped the full user journey from 6pm to 3am before designing a screen." },
      { n: "03", title: "Hand-curated, then automated", body: "Started by editing the feed manually. Wrote the algorithm only after we knew what good looked like." },
      { n: "04", title: "Launched in Brighton first", body: "Saturated Brighton & Hove before considering any other market." },
      { n: "05", title: "Closed the loop with venues", body: "Free dashboard. Venues came to us. Restaurants, bars, clubs — all in." },
    ],
    tech: ["React Native", "Node", "Postgres", "Algolia", "Mapbox", "Stripe", "Mixpanel"],
    proof: [
      { value: "Brighton & Hove", label: "city launch market" },
      { value: "100k+", label: "monthly actives at peak" },
      { value: "Good lessons", label: "on what product-market fit actually means" },
    ],
    learned:
      "Two-sided marketplaces are not solved with code. They're solved with feet, phone calls, and a venue manager who trusts you because you bought them a drink at 11pm. The product was the social layer. The code was just how we kept the social layer running. WhotNow was where I really learned what product-market fit means — not from a textbook, but from watching users open the app on a Friday night.",
    next: "quantumeye",
    prev: "purpleluna",
  },
};

export const CASE_LIST = ["purpleluna", "quantumeye", "whotnow"];
